import { useState, useEffect, useCallback } from 'react';
import { consentManager, ConsentStatus } from '../services/ConsentManager';
import { ConsentData } from '../components/GDPRConsentModal';

export interface UseConsentReturn {
  consentStatus: ConsentStatus;
  hasValidConsent: boolean;
  hasPermission: (permission: 'required' | 'analytics' | 'marketing') => boolean;
  saveConsent: (consentData: ConsentData) => Promise<boolean>;
  withdrawConsent: () => Promise<boolean>;
  clearConsent: () => void;
  isExpiringWithin: (days?: number) => boolean;
  refreshStatus: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * React Hook für DSGVO-Consent-Management
 *
 * Bietet eine einfache API für:
 * - Consent-Status abfragen
 * - Einwilligung speichern/widerrufen
 * - Berechtigungen prüfen
 * - Ablauf-Monitoring
 */
export const useConsent = (): UseConsentReturn => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>({
    hasValidConsent: false,
    isExpired: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Consent-Status laden
  const refreshStatus = useCallback(() => {
    try {
      setError(null);
      const status = consentManager.getValidConsent();
      setConsentStatus(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Laden des Consent-Status');
      console.error('Fehler beim Laden des Consent-Status:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Einwilligung speichern
  const saveConsent = useCallback(async (consentData: ConsentData): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await consentManager.saveConsent(consentData);

      if (success) {
        refreshStatus(); // Status aktualisieren
        return true;
      } else {
        setError('Fehler beim Speichern der Einwilligung');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unbekannter Fehler beim Speichern';
      setError(errorMessage);
      console.error('Fehler beim Speichern der Einwilligung:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [refreshStatus]);

  // Einwilligung widerrufen
  const withdrawConsent = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await consentManager.withdrawConsent();

      if (success) {
        refreshStatus(); // Status aktualisieren
        return true;
      } else {
        setError('Fehler beim Widerrufen der Einwilligung');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unbekannter Fehler beim Widerrufen';
      setError(errorMessage);
      console.error('Fehler beim Widerrufen der Einwilligung:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [refreshStatus]);

  // Einwilligung komplett löschen (ohne Backend-Logging)
  const clearConsent = useCallback(() => {
    try {
      setError(null);
      consentManager.clearConsent();
      refreshStatus(); // Status aktualisieren
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen der Einwilligung');
      console.error('Fehler beim Löschen der Einwilligung:', err);
    }
  }, [refreshStatus]);

  // Berechtigung prüfen
  const hasPermission = useCallback((permission: 'required' | 'analytics' | 'marketing'): boolean => {
    return consentManager.hasPermission(permission);
  }, []);

  // Ablauf-Prüfung
  const isExpiringWithin = useCallback((days: number = 30): boolean => {
    return consentManager.isConsentExpiringWithin(days);
  }, []);

  // Derived state
  const hasValidConsent = consentStatus.hasValidConsent;

  return {
    consentStatus,
    hasValidConsent,
    hasPermission,
    saveConsent,
    withdrawConsent,
    clearConsent,
    isExpiringWithin,
    refreshStatus,
    isLoading,
    error
  };
};

/**
 * Hook-Variante für spezifische Berechtigungen
 * Nützlich für komponenten-spezifische Consent-Checks
 */
export const usePermission = (permission: 'required' | 'analytics' | 'marketing') => {
  const { hasPermission, hasValidConsent, isLoading } = useConsent();

  return {
    hasPermission: hasPermission(permission),
    hasValidConsent,
    isLoading
  };
};

/**
 * Hook für Ablauf-Monitoring
 * Nützlich für Warnungen vor Consent-Ablauf
 */
export const useConsentExpiry = (warningDays: number = 30) => {
  const { consentStatus, isExpiringWithin, hasValidConsent } = useConsent();

  return {
    hasValidConsent,
    isExpiring: isExpiringWithin(warningDays),
    daysUntilExpiry: consentStatus.daysUntilExpiry,
    expiresAt: consentStatus.expiresAt,
    isExpired: consentStatus.isExpired
  };
};

export default useConsent;